import { useMutation } from '@apollo/client/react';
import { Formik } from 'formik';
import NewCommentForm, { newCommentSchema } from './new-comment-form';
import { createGroupCommentMutation, groupPostCommentsQuery } from '../../apollo/groups';
import type { GroupPost } from '../../types/GroupPost';
import { useAppContext } from '../../contexts/app';
import { processMutationError } from '../../utils/formik';
import LoginRequiredNotice from '../login-required-notice';

type Props = {
  groupPost?: GroupPost;
  disabled?: boolean;
};

const NewGroupComment = ({ groupPost, disabled }: Props) => {
  const { isLoggedIn } = useAppContext();
  const [createGroupComment] = useMutation(createGroupCommentMutation, {
    update: (proxy, { data: { createGroupComment: newComment } }) => {
      const groupPostCommentsVariables = { id: groupPost.id };
      const data = proxy.readQuery({
        query: groupPostCommentsQuery,
        variables: groupPostCommentsVariables,
      });
      proxy.writeQuery({
        query: groupPostCommentsQuery,
        data: {
          ...data,
          groupPost: {
            ...data.groupPost,
            comments: [...data.groupPost.comments, newComment],
          },
        },
        variables: groupPostCommentsVariables,
      });
    },
  });
  if (!isLoggedIn) {
    return (
      <LoginRequiredNotice
        returnTo={`/groups/${groupPost.group.slug}/posts/${groupPost.id}`}
        action="comment"
        compact
      />
    );
  }

  if (groupPost.group.archivedAt) {
    return null;
  }

  if (!groupPost.group.membership) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        body: null
      }}
      validationSchema={newCommentSchema}
      onSubmit={(values, actions) => {
        createGroupComment({
          variables: {
            groupPostId: groupPost.id,
            data: values
          },
        }).then(() => {
          actions.setSubmitting(false);
          actions.resetForm();
        }).catch((error) => {
          processMutationError(error, actions);
        });
      }}
    >
      <NewCommentForm disabled={disabled} />
    </Formik>
  );
}

export default NewGroupComment;
